import type {
    DoppeActionType,
    DoppeActionTypeMetadata,
    DoppeActionTypePrepareForViewerOptions,
} from '../doppe-action-types/doppe-action-type';
import type {OmitStrict, PartiallyRequired, RichTextContent, Values, HideableValue } from '@wix/devzai-utils-common';
import {
    assertType,
    enumIsEnumValue,
    enumReduce,
    hideableValueGetVisibleValue,
    objectRemoveEntriesWithUndefinedValues,
    stringIsNullableOrWhiteSpace, assertHideableValue, hideableValueCreateHidden } from '@wix/devzai-utils-common';
import type {DoppeDtoActionAiGenerationMetadata} from '../doppe-dto-action-ai-generation-metadata/doppe-dto-action-ai-generation-metadata';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {assertWixMediaResource} from '@wix/devzai-common-wix';
import {DoppeActionWidgetOpeningMode} from './doppe-action-widget-opening-mode';
import {DoppeActionListItemClickBehaviour} from './doppe-action-list-item-click-behaviour';
import {DoppeActionLockPreviewMode} from './doppe-action-lock-preview-mode';
import type {BymoPagePropsContentSettingsMetadata} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import {
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import LockSettings = DoppeDtoAction.LockSettings;
import SearchSettings = DoppeDtoAction.SearchSettings;

/**
 * Defines how an action is displayed on the page.
 */
export const DoppeDtoActionViewType = {
    /**
     * Button display (default). Shows the action as a clickable button with:
     * - `name` as the button text
     * - `callToActionDescription` as subtitle below the name
     * - `icon` as an optional image on the button
     * When clicked, opens the action content in a card/modal.
     */
    ActionButton: 'ActionButton',

    /**
     * Banner display (premium). Shows the action as a clickable banner with:
     * - `bannerMedia` as the full banner image (user-uploaded custom image)
     * - `name` and `callToActionDescription` are NOT displayed on the banner
     * When clicked, opens the action content in a card/modal (same as ActionButton).
     */
    BannerButton: 'BannerButton',

    /**
     * Card display (UI name: "Card"). Renders the action content directly on the page
     * inside a contained card element with visible boundaries.
     * - `name` and `callToActionDescription` shown in card header (if header is enabled)
     * - No click-to-open behavior - content is immediately visible
     */
    InPage: 'InPage',

    /**
     * On-page display (UI name: "On-page", premium). Renders the action content directly
     * embedded in the page without a container/frame.
     * - `name` and `callToActionDescription` shown as header (if header is enabled)
     * - No click-to-open behavior - content flows naturally with the page
     */
    InPageFrameless: 'InPageFrameless'
} as const;

export type DoppeDtoActionViewType = Values<typeof DoppeDtoActionViewType>;

export const DoppeDtoActionPremiumViewType = enumReduce(DoppeDtoActionViewType, [
    DoppeDtoActionViewType.BannerButton,
    DoppeDtoActionViewType.InPageFrameless
]);

type DoppeDtoActionPremiumViewType = Values<typeof DoppeDtoActionPremiumViewType>;

/**
 * Represents an action (block) on a Bymo page.
 * Actions can be displayed in different view types (Button, Banner, Card, On-page)
 * and contain both global properties (common to all actions) and type-specific settings.
 */
export interface DoppeDtoAction<SETTINGS extends {} = any> {
    /** Unique identifier for the action */
    id: string;

    /** The type of action (e.g., product, link, blog post) */
    actionTypeId: DoppeActionType.Id;

    /**
     * The main title/name of the action.
     *
     * The semantic meaning depends on the action type:
     * - Widget openers (isWidgetOpener=true): Header title of the opened action content.
     *   When the action opens in a card/modal, this is displayed as the header.
     * - Link-only actions (no content): Text displayed on the button itself.
     *   These actions don't open content, so the name IS the button text.
     *
     * Visual display varies by viewType:
     * - Button/Banner views: shown on the button (for link-only) or in opened card header
     * - Card/On-page views: shown in the header (if header is enabled)
     */
    name: string;

    /**
     * Secondary text displayed below the name.
     *
     * The semantic meaning depends on the action type:
     * - Widget openers (isWidgetOpener=true): Subtitle in the opened action header.
     * - Link-only actions: Secondary text on the button.
     *
     * Visual display varies by viewType:
     * - Button view: shown as subtitle below the name
     * - Banner view: NOT displayed
     * - Card/On-page views: shown in the header (if header is enabled)
     */
    callToActionDescription: HideableValue<string>;

    /** Whether the action is visible on the page */
    enabled: boolean;

    /** Whether the action has been deleted (soft delete) */
    deleted: boolean;

    /**
     * Small image displayed on the Button view type.
     * Appears on the left side of the button, next to the name and description.
     * Only visible when the action is in Button view and closed state.
     * Not displayed when the action content is opened.
     */
    icon: HideableValue<WixMediaResource | null>;

    /**
     * Custom banner image for the Banner view type.
     * Replaces the button text entirely with a user-uploaded image.
     * The user can include any text/graphics in the image itself.
     * Only used when viewType is BannerButton.
     */
    bannerMedia: WixMediaResource | null;

    /** Type-specific settings for this action (e.g., product title, price, etc.) */
    settings: SETTINGS;

    /** How the action widget opens when clicked (inline or popup) */
    widgetOpeningMode: DoppeActionWidgetOpeningMode;

    /**
     * Controls how the action is displayed on the page.
     * See {@link DoppeDtoActionViewType} for available options and their behaviors.
     * Defaults to ActionButton if not specified.
     */
    viewType?: DoppeDtoActionViewType;

    /** Whether to show the header (name + callToActionDescription) in Card/On-page views */
    showActionHeader?: boolean;

    isDummyAction?: boolean;
    tag?: string;
    searchSettings: DoppeDtoAction.SearchSettings;
    lockSettings: DoppeDtoAction.LockSettings;

    __aiGenerationMetadata?: DoppeDtoActionAiGenerationMetadata;
}

export function doppeActionIsVisibleInPage (action: DoppeDtoAction): boolean {
    return action.enabled && !action.deleted;
}

export function doppeActionIsHiddenInPage (action: DoppeDtoAction): boolean {
    return !action.enabled && !action.deleted;
}

export function doppeActionIsLockEnabled(action:  Pick<DoppeDtoAction<any>, 'lockSettings'>) {
    return action.lockSettings.enableLock;
}

export function doppeActionIsLockActive(action:  Pick<DoppeDtoAction<any>, 'lockSettings'>) {
    return action.lockSettings.enableLock && action.lockSettings.lockActions.length > 0;
}

export function doppeActionHasLockAction(action: DoppeDtoAction) {
    return action.lockSettings.lockActions.length > 0;
}

const defaultSearchListItemsSettings = {
    indexItemsInSearchType: DoppeActionListItemClickBehaviour.PreviewItem,
    indexItemsInSearch: true,
}

export const DoppeDtoActionSettingsMetadata = {
    callToActionDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    icon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    bannerMedia: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
} satisfies BymoPagePropsContentSettingsMetadata<OmitStrict<DoppeDtoAction, 'id' | 'actionTypeId' | 'settings' | 'viewType' | 'tag' | '__aiGenerationMetadata' | 'searchSettings' | 'lockSettings'>>

export function doppeDtoActionExtendWithDefaultProps (action: PartiallyRequired<Partial<DoppeDtoAction>, 'id' | 'actionTypeId' | 'settings' | 'name' | 'enabled'>) {
    return {
        showActionHeader: true,
        widgetOpeningMode: DoppeActionWidgetOpeningMode.Inline,
        icon: null,
        bannerMedia: null,
        deleted: false,
        callToActionDescription: hideableValueCreateHidden('Description'),

        // Important: Default values for newly added properties should be added here.

        ...objectRemoveEntriesWithUndefinedValues(action),

        lockSettings: {
            ...doppeDtoActionGetDefaultLockSettingsProps(),
            ...action.lockSettings
        },
        searchSettings: {
            ...doppeDtoActionGetDefaultSearchSettingsProps(),
            ...action.searchSettings
        },
    } satisfies DoppeDtoAction;
}

export function doppeDtoActionGetDefaultSearchListSettingsProps () {
    return defaultSearchListItemsSettings;
}

export function doppeDtoActionGetDefaultSearchSettingsProps () {
    return {
        indexInSearch: true,
        indexInSearchType: DoppeActionListItemClickBehaviour.PreviewItem,
        keyword: hideableValueCreateHidden(''),
        title: hideableValueCreateHidden(''),
        description: hideableValueCreateHidden(''),
        image: hideableValueCreateHidden(null)
    } satisfies SearchSettings;
}

export function doppeDtoActionGetDefaultLockSettingsProps () {
    return {
        lockActions: [],
        enableLock: false,
        customLockPreviewTitle: '',
        customLockPreviewDescription: '',
        customLockPreviewMedia: null,
        customLockPreviewButtonText: hideableValueCreateHidden(''),
        customPreviewContent: false,
        lockPreviewShowActionHeader: true,
        lockPreviewMode: DoppeActionLockPreviewMode.Custom,
        lockPreviewInnerCtaButtonText: hideableValueCreateHidden(''),
    } satisfies LockSettings;
}



export namespace DoppeDtoAction {
    export type Id = DoppeDtoAction['id'];

    export type SearchSettings = {
        indexInSearch: boolean;
        indexInSearchType: DoppeActionListItemClickBehaviour;
        keyword: HideableValue<string>;
        title: HideableValue<string>;
        description: HideableValue<string>;
        image: HideableValue<WixMediaResource | null>;
    }

    export type LockSettings = {
        lockActions: DoppeDtoAction<any>[];
        enableLock: boolean;
        customPreviewContent: boolean;
        customLockPreviewButtonText: HideableValue<string>,
        customLockPreviewTitle: HideableValue<string>,
        customLockPreviewDescription: HideableValue<RichTextContent>,
        customLockPreviewMedia: WixMediaResource | null,
        lockPreviewShowActionHeader: boolean,
        lockPreviewMode: DoppeActionLockPreviewMode;
        lockPreviewInnerCtaButtonText: HideableValue<string>;
    }

    export type ListSearchSettings = {
        indexItemsInSearch: boolean;
        indexItemsInSearchType: DoppeActionListItemClickBehaviour;
    }

    export type ActionListItem<SETTINGS extends {}> = {
        title: string;
        description: HideableValue<RichTextContent>;
        image: WixMediaResource | null;
        ribbon: HideableValue<string>
        couponCode: HideableValue<string>;
        action: DoppeDtoAction<SETTINGS>;
        itemId: string;
        url: string | null;
        enabled: boolean;
        deleted: boolean;
        displayInSearch: boolean;
    }
}

export function assertDoppeDtoAction(action: DoppeDtoAction): asserts action is DoppeDtoAction;
export function assertDoppeDtoAction(action: unknown): asserts action is DoppeDtoAction;
export function assertDoppeDtoAction(action: unknown): asserts action is DoppeDtoAction {
    assertType<DoppeDtoAction<{}>>(action, assert => {
        assert.isObject({
            id: assert => assert.isString(),
            actionTypeId: assert => assert.isString(),
            name: assert => assert.isString(),

            enabled: assert => assert.isBoolean(),
            deleted: assert => assert.isBoolean(),
            widgetOpeningMode: assert => assert.isString(),
            callToActionDescription: assert => assert.isUnion(
                assert => assert.isString(),
                assert => assert.usingAssertionFunction(value => {
                    return assertHideableValue<string>(value, assert => assert.isString())
                }),
            ),
            icon: assert => assert.usingAssertionFunction(value => {
                assertHideableValue<WixMediaResource | null>(value, assert => {
                    assert.isUnion(
                        assert => assert.isNull(),
                        assert => assert.usingAssertionFunction(assertWixMediaResource)
                    )
                })
            }),
            bannerMedia: assert => assert.isUnion(
                assert => assert.isNull(),
                assert => assert.usingAssertionFunction(assertWixMediaResource)
            ),
            settings: assert => assert.isObject({}, {
                assertNoUnknownKeys: false
            }),
            viewType: assert => assert.optional.isOneOfValues(Object.values(DoppeDtoActionViewType)),
            showActionHeader: assert => assert.optional.isBoolean(),
            isDummyAction: assert => assert.optional.isBoolean(),
            tag: assert => assert.optional.isString(),
            __aiGenerationMetadata: assert => assert.optional.isAny(),
            lockSettings: assert => assert.isAny(),
            searchSettings: assert => assert.isAny(),
            // image: assert => assert.usingAssertionFunction(value => {
            //     assertDoppeHideableValue<WixMediaResource | null>(value, assert => {
            //         assert.isUnion(
            //             assert => assert.isNull(),
            //             assert => assert.usingAssertionFunction(assertWixMediaResource)
            //         )
            //     })
            // }),
            // indexInSearch: assert => assert.isBoolean(),
            // indexInSearchType: assert => assert.isString(),
            // keyword: assert => assert.usingAssertionFunction(value => {
            //     return assertDoppeHideableValue<string>(value, assert => assert.isString())
            // }),
            // title: assert => assert.usingAssertionFunction(value => {
            //     return assertDoppeHideableValue<string>(value, assert => assert.isString())
            // }),
            // description: assert => assert.usingAssertionFunction(value => {
            //     return assertDoppeHideableValue<string>(value, assert => assert.isString())
            // }),
        }, {
            // TODO: It's better to remove all the unknown keys of an action using a migration.
            assertNoUnknownKeys: false
        })
    })
}

export function doppeActionIsOfType<T extends {}> (
    doppeAction: DoppeDtoAction,
    doppeActionType: DoppeActionTypeMetadata<T>
) : doppeAction is DoppeDtoAction<T> {
    return doppeAction.actionTypeId === doppeActionType.id;
}

export function doppeActionResolveSettings<T extends {}> (
    doppeAction: DoppeDtoAction<T>,
    actionTypeMetadata: DoppeActionTypeMetadata<T>
) {
    return actionTypeMetadata.resolveActionSettings(doppeAction)
}

export function doppeDtoActionShouldShowActionHeader (action: DoppeDtoAction) {

    const showActionHeader = action.showActionHeader ?? true;

    if (showActionHeader) {

        if (stringIsNullableOrWhiteSpace(action.name) && stringIsNullableOrWhiteSpace(hideableValueGetVisibleValue(action.callToActionDescription, null))) {
            if (action.viewType === DoppeDtoActionViewType.InPageFrameless || action.viewType === DoppeDtoActionViewType.InPage) {
                return false;
            }
        }

        return true;
    } else {
        return false;
    }

}

export function doppeDtoActionViewTypeIsDisplayedAsButton (viewType: DoppeDtoActionViewType | undefined) {
    // Note: We compare also to undefined because the default view type is ActionButton
    return viewType === undefined || viewType === DoppeDtoActionViewType.ActionButton || viewType === DoppeDtoActionViewType.BannerButton;
}

export function doppeDtoActionIsWidgetShownAsPopup (action: DoppeDtoAction) {
    return doppeDtoActionViewTypeIsDisplayedAsButton(action.viewType) && action.widgetOpeningMode === DoppeActionWidgetOpeningMode.Popup;
}

export function doppeDtoActionViewTypeIsPremium (viewType: DoppeDtoActionViewType | undefined) : viewType is DoppeDtoActionPremiumViewType {

    const normalizedViewType = viewType ?? DoppeDtoActionViewType.ActionButton;

    return enumIsEnumValue(DoppeDtoActionPremiumViewType, normalizedViewType);
}

export function doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction<T extends {}>(actionSettings: T, removeUrlsCallback: (actionSettings: T) => void, options: DoppeActionTypePrepareForViewerOptions) {
    const {
        omitLockedActionsSettings,
        isLocked,
        isInnerCtaLocked
    } = options;

    if (omitLockedActionsSettings && isInnerCtaLocked && isLocked) {
        removeUrlsCallback(actionSettings);
    }
}